import { renderLinearGradient } from './linear.js';
import { renderRadialGradient } from './radial.js';
import { renderConicGradient } from './conic.js';
import { renderRepeatingLinearGradient } from './repeating-linear.js';
import { renderRepeatingRadialGradient } from './repeating-radial.js';
import { renderRepeatingConicGradient } from './repeating-conic.js';

/**
 * Map of gradient types to their renderer functions
 */
const renderers = {
  'linear-gradient': renderLinearGradient,
  'radial-gradient': renderRadialGradient,
  'conic-gradient': renderConicGradient,
  'repeating-linear-gradient': renderRepeatingLinearGradient,
  'repeating-radial-gradient': renderRepeatingRadialGradient,
  'repeating-conic-gradient': renderRepeatingConicGradient,
};

/**
 * Canvas Gradient Cache
 * Caches CanvasGradient objects by (gradientDef hash + bounds)
 * Note: CanvasGradient objects are tied to a specific context, so we use WeakMap
 * to automatically clean up when context is garbage collected
 */
class GradientRenderCache {
  constructor(maxSizePerContext = 256) {
    // WeakMap: context -> Map<cacheKey, CanvasGradient>
    this.contextCaches = new WeakMap();
    this.maxSizePerContext = maxSizePerContext;
    this.stats = {
      hits: 0,
      misses: 0,
    };
  }

  /**
   * Generate cache key from gradient definition and bounds
   * Optimized for speed: uses simple string concatenation
   */
  generateKey(gradientDef, bounds) {
    // Fast path for common linear gradients
    const type = gradientDef.type;
    const orientation = gradientDef.orientation;

    let orientationKey = '';
    if (orientation) {
      if (Array.isArray(orientation)) {
        orientationKey = orientation.map((o) => `${o.type}:${o.value}`).join('|');
      } else {
        orientationKey = `${orientation.type}:${orientation.value}`;
      }
    }

    // Color stops key - most important for uniqueness
    const stopsKey = gradientDef.colorStops
      .map((stop) => {
        const color =
          stop.type === 'hex'
            ? stop.value
            : stop.type === 'rgba' || stop.type === 'rgb'
              ? stop.value.join(',')
              : stop.value;
        const len = stop.length ? `${stop.length.value}${stop.length.type}` : '';
        return `${color}@${len}`;
      })
      .join(';');

    // Bounds key - rounded to reduce cache fragmentation
    const boundsKey = `${Math.round(bounds.w)}x${Math.round(bounds.h)}`;

    return `${type}|${orientationKey}|${stopsKey}|${boundsKey}`;
  }

  get(context, gradientDef, bounds) {
    const contextCache = this.contextCaches.get(context);
    if (!contextCache) {
      this.stats.misses++;
      return null;
    }

    const key = this.generateKey(gradientDef, bounds);
    const cached = contextCache.get(key);

    if (cached) {
      this.stats.hits++;
      return cached;
    }

    this.stats.misses++;
    return null;
  }

  set(context, gradientDef, bounds, gradient) {
    let contextCache = this.contextCaches.get(context);

    if (!contextCache) {
      contextCache = new Map();
      this.contextCaches.set(context, contextCache);
    }

    // Evict oldest entries if at capacity
    if (contextCache.size >= this.maxSizePerContext) {
      const firstKey = contextCache.keys().next().value;
      contextCache.delete(firstKey);
    }

    const key = this.generateKey(gradientDef, bounds);
    contextCache.set(key, gradient);
  }

  getStats() {
    const total = this.stats.hits + this.stats.misses;
    return {
      ...this.stats,
      hitRate: total > 0 ? this.stats.hits / total : 0,
    };
  }

  clearStats() {
    this.stats.hits = 0;
    this.stats.misses = 0;
  }
}

// Global render cache
const renderCache = new GradientRenderCache(256);

/**
 * Render a gradient based on its type
 * @param {CanvasRenderingContext2D} context - Canvas context
 * @param {Object} gradientDef - Gradient definition from parser
 * @param {Object} bounds - Bounding box { x, y, w, h }
 * @param {Function} [render] - Optional render callback
 * @returns {CanvasGradient|null} Canvas gradient object or null
 */
export function renderGradient(context, gradientDef, bounds, render) {
  if (!bounds.w || !bounds.h) return null;

  const renderer = renderers[gradientDef.type];

  if (!renderer) {
    // Unsupported gradient type
    return null;
  }

  // Try cache first (only when bounds are at origin - common case)
  // We skip caching for translated bounds as the gradient would need recalculation anyway
  const canCache = bounds.x === 0 && bounds.y === 0;
  let gradient = null;

  if (canCache) {
    gradient = renderCache.get(context, gradientDef, bounds);
  }

  if (!gradient) {
    gradient = renderer(context, gradientDef, bounds);
    if (canCache && gradient) {
      renderCache.set(context, gradientDef, bounds, gradient);
    }
  }

  if (render && gradient) {
    context.fillStyle = gradient;
    render(gradient);
  }

  return gradient;
}

/**
 * Get render cache statistics
 */
export function getRenderStats() {
  return renderCache.getStats();
}

/**
 * Clear render cache statistics (cache itself is managed by WeakMap)
 */
export function clearRenderStats() {
  renderCache.clearStats();
}

export {
  renderLinearGradient,
  renderRadialGradient,
  renderConicGradient,
  renderRepeatingLinearGradient,
  renderRepeatingRadialGradient,
  renderRepeatingConicGradient,
};
